// ** React Imports
import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react'

// ** Redux
import { useDispatch, useSelector } from 'react-redux'

// ** Third Party Imports
import moment from 'moment'

// ** MUI Imports
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

// ** Icons Imports
import BellOutline from 'mdi-material-ui/BellOutline'
import Refresh from 'mdi-material-ui/Refresh'
import AlertCircleOutline from 'mdi-material-ui/AlertCircleOutline'
import AlertOutline from 'mdi-material-ui/AlertOutline'
import CheckCircleOutline from 'mdi-material-ui/CheckCircleOutline'
import InformationOutline from 'mdi-material-ui/InformationOutline'
import ReportIcon from '@mui/icons-material/Report'
import CheckIcon from '@mui/icons-material/Check'
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert'
import VisibilityIcon from '@mui/icons-material/Visibility'

// ** Custom Components Imports
import CustomChip from '../../../components/mui/chip'

// ** Toast Imports
import toast, { Toaster } from 'react-hot-toast'

// ** Local imports
import NotificationSubscriber from 'components/notifications/NotificationSubscriber'
import {
  acknowledgeNotification,
  fetchNotificationsCenter,
  notificationsSelector,
  unreadByCustomerSelector,
  unreadNotificationsSelector
} from 'store/notifications'
import { createTicketFromNotification, notifyTicketsChanged } from 'utils/anomalyTickets'
import { useAuth } from 'hooks/useAuth'
import { useNavigate } from 'react-router-dom'

const ScrollContainer = styled('div')(({ theme }) => ({
  maxHeight: 360,
  overflowY: 'auto',
  overflowX: 'hidden',
  '&::-webkit-scrollbar': {
    width: 6
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[400],
    borderRadius: 6
  }
}))

const EmptyState = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
    <img height={160} width={160} src='/grey-planet.svg' alt='empty-notifications' />
    <Typography sx={{ mt: 3, fontWeight: 600, color: 'text.secondary' }}>You're all caught up!</Typography>
  </Box>
)

const NotificationFlyAnimation = ({ targetRect, onComplete }) => {
  const indicatorRef = useRef(null)

  useEffect(() => {
    if (!indicatorRef.current || !targetRect || typeof window === 'undefined') return undefined
    const startX = window.innerWidth - 80
    const startY = window.innerHeight - 80
    const endX = targetRect.left + targetRect.width / 2
    const endY = targetRect.top + targetRect.height / 2

    const animation = indicatorRef.current.animate(
      [
        {
          left: `${startX}px`,
          top: `${startY}px`,
          transform: 'translate(-50%, -50%) scale(1)',
          opacity: 0.95
        },
        {
          left: `${endX}px`,
          top: `${endY}px`,
          transform: 'translate(-50%, -50%) scale(0.2)',
          opacity: 0
        }
      ],
      {
        duration: 700,
        easing: 'ease-in-out',
        fill: 'forwards'
      }
    )

    animation.onfinish = () => {
      onComplete?.()
    }

    return () => animation.cancel()
  }, [targetRect, onComplete])

  if (!targetRect) return null

  return (
    <Box
      ref={indicatorRef}
      sx={{
        position: 'fixed',
        width: 18,
        height: 18,
        borderRadius: '50%',
        backgroundColor: 'primary.main',
        boxShadow: theme => theme.shadows[6],
        pointerEvents: 'none',
        zIndex: theme => theme.zIndex.snackbar + 5
      }}
    />
  )
}

const humanizeCustomerLabel = customer => customer?.organization || customer?.domain || `Customer ${customer?.id}`

const resolveToastMessage = notification => notification?.message || 'Invoice notification'

const STATUS_VISUALS = {
  FAILURE: { color: 'error.main', chipColor: 'error', Icon: CrisisAlertIcon },
  ERROR: { color: 'error.main', chipColor: 'error', Icon: AlertCircleOutline },
  SKIPPED: { color: 'warning.main', chipColor: 'warning', Icon: AlertOutline },
  IN_PROGRESS: { color: 'primary.main', chipColor: 'primary', Icon: Refresh },
  SUCCESS: { color: 'success.main', chipColor: 'success', Icon: CheckCircleOutline },
  RESOLVED: { color: 'success.main', chipColor: 'success', Icon: CheckCircleOutline },
  default: { color: 'text.primary', chipColor: 'info', Icon: InformationOutline }
}

const resolveStatusVisual = status => STATUS_VISUALS[status] || STATUS_VISUALS.default

const NotificationDropdown = ({ settings }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [customerFilter] = useState(null)
  const [flyAnimation, setFlyAnimation] = useState(null)

  const dispatch = useDispatch()
  const hidden = useMediaQuery(theme => theme.breakpoints.down('lg'))
  const bellRef = useRef(null)
  const { user } = useAuth()
  const navigate = useNavigate()

  // ** Selectors
  const { items, loading, error } = useSelector(notificationsSelector)
  const [unreadTotal, setUnreadTotal] = useState(0)
  const unreadByCustomer = useSelector(unreadByCustomerSelector)

  useEffect(() => {
    dispatch(
      fetchNotificationsCenter({
        customerId: customerFilter,
        limit: 25
      })
    )
  }, [dispatch, customerFilter])

  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget)
    handleRefresh()
    refreshUnread()
  }

  const handleDropdownClose = () => {
    setAnchorEl(null)
    handleRefresh()
    refreshUnread()
  }

  const handleRefresh = () => {
    dispatch(
      fetchNotificationsCenter({
        customerId: customerFilter,
        limit: 25
      })
    )
  }

  const refreshUnread = () => {
    const unreadCount = items?.reduce((acc, notification) => {
      if (!notification.readByUser) {
        return acc + 1
      }
      return acc
    }, 0)
    setUnreadTotal(prevUnreadTotal => Math.max(Math.max(prevUnreadTotal - 1, unreadCount), 0))
  }

  const handleMarkRead = (event, notificationId) => {
    event && event.stopPropagation()
    dispatch(acknowledgeNotification(notificationId, user.email))
  }

  const triggerFlyAnimation = useCallback(() => {
    if (!bellRef.current) return
    const rect = bellRef.current.getBoundingClientRect()
    setFlyAnimation({ rect, key: Date.now() })
  }, [])

  const handleRealtimeNotification = useCallback(
    notification => {
      const message = resolveToastMessage(notification)
      toast(message, {
        position: 'bottom-right',
        duration: 5000
      })
      triggerFlyAnimation()
    },
    [triggerFlyAnimation]
  )

  const handleTicketCreation = async (e, notification, targetStatus) => {
    e.stopPropagation()
    const processingToastId = toast.loading('Processing ticket...')
    try {
      const resolutionReason =
        targetStatus === 'resolved'
          ? notification?.metadata?.resolutionReason || 'Resolved directly from notification center.'
          : undefined

      const createdTicket = await createTicketFromNotification(notification, targetStatus, {
        resolutionReason
      })

      notifyTicketsChanged()
      handleRefresh()
      handleMarkRead(null, notification.id)

      toast.dismiss(processingToastId)
      toast.success(
        `Ticket ${createdTicket?.id || ''} ${
          targetStatus === 'resolved' ? 'resolved from notification.' : 'created from notification.'
        }`
      )
    } catch (error) {
      toast.dismiss(processingToastId)
      const message = error?.message || 'Failed to create ticket'
      toast.error(message)
    }
  }

  const handleRedirect = (e, notificationId, customerId) => {
    e.stopPropagation()
    dispatch(acknowledgeNotification(notificationId, user.email))
    navigate(`/billing/${customerId}`)

    handleDropdownClose()
  }

  useEffect(() => {
    refreshUnread()
  }, [items])

  const ScrollWrapper = ({ children }) => {
    if (hidden) {
      return <Box sx={{ maxHeight: 360, overflowY: 'auto', overflowX: 'hidden' }}>{children}</Box>
    }

    return <ScrollContainer>{children}</ScrollContainer>
  }

  return (
    <Fragment>
      <NotificationSubscriber onNotification={handleRealtimeNotification} />
      <Toaster position='bottom-right' />
      {flyAnimation ? (
        <NotificationFlyAnimation
          key={flyAnimation.key}
          targetRect={flyAnimation.rect}
          onComplete={() => setFlyAnimation(null)}
        />
      ) : null}
      <IconButton
        aria-haspopup='true'
        aria-label='Notifications'
        onClick={handleDropdownOpen}
        aria-controls='notifications-menu'
        ref={bellRef}
      >
        <Badge color='error' badgeContent={unreadTotal} overlap='circular' max={99}>
          <BellOutline sx={{ color: 'text.secondary' }} />
        </Badge>
      </IconButton>
      <Menu
        id='notifications-menu'
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDropdownClose}
        PaperProps={{
          sx: {
            width: 420,
            borderRadius: 2,
            mt: 2
          }
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: settings.direction === 'ltr' ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: settings.direction === 'ltr' ? 'right' : 'left' }}
      >
        <Box sx={{ px: 4, pt: 4, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 600 }}>Notifications</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CustomChip
                skin='light'
                size='small'
                label={unreadTotal > 0 ? 'New' : 'No unread'}
                color={'primary'}
                sx={{ borderRadius: '10px' }}
              />
              <IconButton size='small' onClick={handleRefresh} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : <Refresh fontSize='small' />}
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ color: '#EEEEEE' }} />
        {error && (
          <Box sx={{ px: 4, py: 2 }}>
            <Typography color='error' variant='body2'>
              {error}
            </Typography>
          </Box>
        )}
        <ScrollWrapper>
          {items.length === 0 && !loading ? (
            <EmptyState />
          ) : (
            items.map((notification, i) => {
              const statusVisual = resolveStatusVisual(notification.status)
              const StatusIcon = statusVisual.Icon

              return (
                <MenuItem
                  sx={{
                    paddingTop: 4,
                    paddingBottom: 4,
                    borderBottom: i === items.length - 1 ? '' : `1px solid #DDDDDD`,
                    alignItems: 'flex-start',
                    backgroundColor: notification.readByUser ? 'transparent' : '#EEEEEE'
                  }}
                  key={notification.id}
                  onClick={event => handleRedirect(event, notification.id, notification.customerId)}
                  disableRipple
                >
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5 }}>
                          <StatusIcon sx={{ color: statusVisual.color, fontSize: '1.1rem' }} />
                          <Typography
                            sx={{
                              whiteSpace: 'break-spaces',
                              fontWeight: notification.read ? 500 : 700,
                              fontSize: '0.925rem'
                              // color: statusVisual.color
                            }}
                          >
                            {notification.message}
                          </Typography>
                        </Box>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary', mt: 0.5, whiteSpace: 'break-spaces' }}
                        >
                          {notification.metadata?.customerName ||
                            humanizeCustomerLabel({ id: notification.customerId })}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        mt: 1,
                        gap: 1
                      }}
                    >
                      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                        {moment(notification.createdAt).format('MMM D, YYYY h:mm A')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {notification.hasTicket ? (
                          <></>
                        ) : (
                          <>
                            <Button
                              size='small'
                              variant='outlined'
                              color='warning'
                              sx={{
                                p: '0.25rem !important',
                                width: 'fit-content',
                                minWidth: 'fit-content',
                                borderRadius: '50%'
                              }}
                              disabled={notification.hasTicket}
                              onClick={event => handleTicketCreation(event, notification, 'open')}
                            >
                              <ReportIcon fontSize='small' />
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              color='success'
                              sx={{
                                p: '0.25rem !important',
                                width: 'fit-content',
                                minWidth: 'fit-content',
                                borderRadius: '50%'
                              }}
                              disabled={notification.hasTicket}
                              onClick={event => handleTicketCreation(event, notification, 'resolved')}
                            >
                              <CheckIcon fontSize='small' />
                            </Button>
                          </>
                        )}
                        {!notification.readByUser && (
                          <Button
                            size='small'
                            variant='text'
                            sx={{
                              p: '0.25rem !important',
                              width: 'fit-content',
                              minWidth: 'fit-content',
                              borderRadius: '50%',
                              border: '1.5px solid #1976d2'
                            }}
                            onClick={event => handleMarkRead(event, notification.id)}
                          >
                            <VisibilityIcon fontSize='small' />
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </MenuItem>
              )
            })
          )}
        </ScrollWrapper>
        <Divider sx={{ color: '#EEEEEE' }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <Button variant='contained' onClick={handleDropdownClose}>
            Close
          </Button>
        </Box>
      </Menu>
    </Fragment>
  )
}

export default NotificationDropdown
